/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';
import { Box, Grid, MenuItem, TextField } from '@mui/material';
import { CompanyDetailsForm } from '../types/CompanyDetailsForm';
import MapsAutoComplete from './MapsAutoComplete';
import RetailerDetailMap from './RetailerDetailMap';
import { OnboardingStatusEnum } from '../enums/OnboardingStatusEnum';
import { UserFranchiseList } from '../../../../app/services/api/ofdClient';

interface OnboardingCompanyDetailsProps {
  formData: CompanyDetailsForm;
  handleChange: Function;
  updateAddressFields: Function;
  applicationStatus: number;
  userFranchiseList: UserFranchiseList;
}

const OnboardingCompanyDetails: React.FC<OnboardingCompanyDetailsProps> = ({ formData, handleChange, updateAddressFields, applicationStatus, userFranchiseList }) => {
  const { t } = useTranslation('onboardingWizard');
  const textFieldMargin = { mb: 8 };
  const isDraft = applicationStatus == OnboardingStatusEnum.DRAFT;

  const list = userFranchiseList?.franchiseNames?.map(f => f.toUpperCase()) ?? [];
  const current = formData.franchiseName?.toUpperCase();

  const handleAddressSelect = (address: any) => {
    updateAddressFields(address);
  };

  const setNewAddressInput = (value: string) => {
    handleChange({ target: { value } }, 'addressLine1');
  };

  return (
    <Box sx={{ p: 6, paddingBottom: 0 }}>
      <Grid container
        spacing={3}>
        <Grid item
          xs={12}
          sx={{ pr: 4 }}
          md={6}>
          <TextField
            fullWidth
            required
            label={t('companyTab.name')}
            onChange={(e) => handleChange(e, 'name')}
            value={formData.name}
            sx={textFieldMargin}
            disabled={!isDraft}
          />
          <TextField select
            required
            fullWidth
            disabled={!isDraft || (!!current && !list.includes(current))}
            label={t('companyTab.franchiseName')}
            value={formData.franchiseName?.toUpperCase() ?? ''}
            onChange={(e) => handleChange(e, 'franchiseName')}
            sx={textFieldMargin}>
            {current && !list.includes(current) ? (
              <MenuItem  
                key={current}
                value={current}>
                {current}
              </MenuItem> 
            ) : list.map((option) => (
              <MenuItem  
                key={option}
                value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <MapsAutoComplete
            address={formData.addressLine1 ?? ''}
            onAddressSelect={handleAddressSelect}
            setNewAddressInput={setNewAddressInput}
          />     
          <TextField
            fullWidth
            label={t('companyTab.addressLine2')}
            value={formData.addressLine2}
            onChange={(e) => handleChange(e, 'addressLine2')}
            sx={textFieldMargin}
          />
          <Box sx={{ width: '100%', height: '300px', marginBottom: '24px' }}>
            <RetailerDetailMap
              retailer={formData} />
          </Box>
        </Grid>

        <Grid item
          sx={{ pl: 4 }}
          xs={12}
          md={6}>
          <TextField
            fullWidth
            required
            label={t('companyTab.city')}
            value={formData.city}
            onChange={(e) => handleChange(e, 'city')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            required
            label={t('companyTab.postalCode')}
            value={formData.postalCode}
            onChange={(e) => handleChange(e, 'postalCode')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            required
            label={t('companyTab.country')}
            value={formData.country}
            onChange={(e) => handleChange(e, 'country')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            required
            label={t('companyTab.region')}
            value={formData.region}
            onChange={(e) => handleChange(e, 'region')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            label={t('companyTab.parentCompany')}
            value={formData.parentCompany}
            onChange={(e) => handleChange(e, 'parentCompany')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            required
            rows={4}
            label={t('companyTab.directors')}
            value={formData.directors}
            onChange={(e) => handleChange(e, 'directors')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            required
            label={t('companyTab.awrsNumber')}
            value={formData.awrsnumber}
            onChange={(e) => handleChange(e, 'awrsnumber')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            required
            label={t('companyTab.eoriNumber')}
            value={formData.eorinumber}
            onChange={(e) => handleChange(e, 'eorinumber')}
            sx={textFieldMargin}
          />
          <TextField
            fullWidth
            required
            label={t('companyTab.vatNumber')}
            value={formData.vatnumber}
            onChange={(e) => handleChange(e, 'vatnumber')}
            sx={textFieldMargin}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default OnboardingCompanyDetails;